import { useContext, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { Skeleton } from '@repo/videa-components/src/ui/skeleton';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { IframeMessageType, ModalType } from '@/app/external/types';
import { useLocalizedFormatDate } from '@/modules/claims/summary/state';
import { type TVisitSummary } from '@/react-query/useVisitSummaries';
import { useIsEmbedded } from '@/utils/hooks';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ShareWithPatientDropdown } from '../compare/ShareWithPatientDropdown';
import { InsuranceClaimFooter } from '../findingSidebar/insuranceClaimFooter/InsuranceClaimFooter';
import { PatientEducationDialog } from '../patientEducationDialog/PatientEducationDialog';

type VisitSidebarFooterProps = {
  activeImageId?: string;
  dob?: string;
  isInsuranceClaimLoading: boolean;
  isLoading: boolean;
  onOpenInsuranceClaimPdf?: () => void;
  patientName?: string;
  visitSummary?: TVisitSummary;
};

export const VisitSidebarFooter = ({
  activeImageId,
  dob,
  isInsuranceClaimLoading,
  isLoading,
  onOpenInsuranceClaimPdf,
  patientName,
  visitSummary,
}: VisitSidebarFooterProps) => {
  const { isIframe, patientId, imageId, externalPatientId, externalImageId } =
    useContext(ExternalPatientContext);
  const [isPatientEducationDialogOpen, setIsPatientEducationDialogOpen] =
    useState(false);
  const isEmbedded = useIsEmbedded();
  const formatDate = useLocalizedFormatDate();
  const { trackEvent } = useTrackEvent();
  const { id: selectedPatientId } = useParams<{ id: string }>();

  return (
    <>
      <PatientEducationDialog
        isOpen={isPatientEducationDialogOpen}
        setIsOpen={setIsPatientEducationDialogOpen}
        imageId={activeImageId}
      />

      <div
        className={classNames(
          'vai-flex vai-flex-col',
          'vai-bg-sidebar-backgroundDark vai-py-4 [@media(max-height:570px)]:vai-py-2 vai-px-3 [@media(max-height:570px)]:vai-px-2',
          'vai-border-t vai-border-sidebar-border',
          {
            'vai-gap-4': !isIframe,
            'vai-gap-2 [@media(max-height:570px)]:vai-gap-1': isIframe,
          },
        )}
      >
        {/* Patient education buttons */}
        <div className="vai-grid vai-grid-cols-1 lg:vai-grid-cols-2 [@media(max-height:570px)]:vai-grid-cols-2 vai-gap-2">
          {isLoading ? (
            <>
              <Skeleton className="vai-w-full vai-h-10" />
              <Skeleton className="vai-w-full vai-h-10" />
            </>
          ) : (
            <>
              <Button
                data-testid="patient-education-button"
                variant="default"
                size="default"
                className="vai-flex vai-flex-1 !vai-px-2 vai-py-3 [@media(max-height:570px)]:!vai-py-2 !vai-whitespace-normal !vai-h-auto"
                onClick={() => {
                  trackEvent(
                    mixpanelEvents.visitSidebarPatientEducationToggle,
                    {
                      source: 'visit_sidebar',
                      estimatedVisitId: visitSummary?.estimatedVisitId,
                      unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
                      isIframe: !!isIframe,
                      externalPatientId,
                      externalImageId,
                      patientId: selectedPatientId ?? patientId,
                      imageId: activeImageId,
                    },
                  );

                  if (isIframe) {
                    window.parent.postMessage(
                      {
                        type: IframeMessageType.MODAL_OPEN,
                        payload: {
                          type: ModalType.PATIENT_EDUCATION,
                          patientId,
                          imageId,
                          externalPatientId,
                          externalImageId,
                        },
                      },
                      '*',
                    );
                    return;
                  }

                  setIsPatientEducationDialogOpen(true);
                }}
              >
                <span className="vai-text-sm [@media(max-height:570px)]:vai-text-[12px] vai-font-medium vai-max-w-[140px] vai-text-center vai-break-words vai-leading-tight">
                  <Trans>Patient Education</Trans>
                </span>
              </Button>
              <ShareWithPatientDropdown
                hasActiveImage={!!activeImageId}
                source="visit_sidebar"
              />
            </>
          )}
        </div>

        {isIframe && (
          <p className="vai-font-inter vai-text-xs vai-text-zinc-500 vai-text-center">
            <Trans>
              Patient View visualization is intended for patient education.
            </Trans>
          </p>
        )}

        {!isEmbedded && !isIframe && (
          <InsuranceClaimFooter
            estimatedVisitId={
              visitSummary?.estimatedVisitId
                ? String(visitSummary?.estimatedVisitId)
                : ''
            }
            isLoading={isInsuranceClaimLoading}
            patientInfo={{
              name: patientName ?? '',
              formattedDob: formatDate(dob ?? '', true),
              dob,
            }}
            onToggleIsInsuranceClaimPdfOpen={onOpenInsuranceClaimPdf}
          />
        )}
      </div>
    </>
  );
};
